import { roofPanelPhoto } from "@/lib/utils";
import { useEffect, useRef, useState, useCallback, Fragment } from "react";
import { APIProvider, Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { api, formatApiError, cantiereAddress } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Map as MapIcon, Search, Plus, Check, Trash2, Loader2, Save, Pentagon, TriangleAlert, Ruler, X, Grid3x3, Move, CheckCircle2, ArrowRight, ArrowLeft, Maximize2, Minimize2, FileUp } from "lucide-react";
import { ListinoPanelPicker } from "./ListinoPanelPicker";
import { FreePanelPicker } from "./FreePanelPicker";
import { AestheticPanelPicker } from "./AestheticPanelPicker";
import RoofMapView from "./RoofMapView";
import { hasDrawing, roofBgUrl } from "./RoofPdfView";
import FlatRoofControls from "./FlatRoofControls";
import { PanelInUse } from "./PanelInUse";
import PanelLayoutEditor from "./PanelLayoutEditor";

const uid = () => Math.random().toString(36).slice(2, 9);
const FALDA_COLORS = ["#FDD003", "#3B82F6", "#10B981", "#EF4444", "#A855F7", "#F97316"];
const EXCL_COLOR = "#DC2626";
const num = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: 1 }));

const ORIENT = [
  { v: 180, l: "Sud" }, { v: 135, l: "Sud-Est" }, { v: 225, l: "Sud-Ovest" },
  { v: 90, l: "Est" }, { v: 270, l: "Ovest" }, { v: 45, l: "Nord-Est" },
  { v: 315, l: "Nord-Ovest" }, { v: 0, l: "Nord" },
];
const EXCL_TIPI = [
  { v: "ostacolo", l: "Ostacolo" }, { v: "lucernario", l: "Lucernario" }, { v: "zona_non_utilizzabile", l: "Zona non utilizzabile" },
];
const ANNOT = {
  cabina: { color: "#F97316", label: "Cabina elettrica" },
  cavidotto: { color: "#38BDF8", label: "Cavidotti" },
  locale_tecnico: { color: "#A855F7", label: "Locale tecnico / inverter" },
  parcheggio: { color: "#22C55E", label: "Area parcheggio mezzi di sollevamento" },
};

// Totale combinato lato client (inclinato + piano) per il riepilogo del CRM interno.
function combinedOf(roof) {
  const plOn = (roof?.panel_layout?.enabled ?? true) !== false;
  const flOn = (roof?.flat_layout?.compatible ?? true) !== false;
  const pl = plOn ? (roof?.panel_layout?.summary || {}) : {};
  const fl = flOn ? (roof?.flat_layout?.summary || {}) : {};
  const incC = Number(pl.count || 0), incK = Number(pl.kwp || 0);
  const piaC = Number(fl.count || 0), piaK = Number(fl.kwp || 0);
  return { count: incC + piaC, kwp: Math.round((incK + piaK) * 100) / 100,
    inc: { count: incC, kwp: Math.round(incK * 100) / 100 }, piano: { count: piaC, kwp: Math.round(piaK * 100) / 100 } };
}

// Live client-side summary (backend è autoritativo al salvataggio)
function clientSummary(falde, moduleArea, moduleWp) {
  let proj = 0, reale = 0, netta = 0;
  falde.forEach((f) => {
    const tilt = Math.max(0, Math.min(f.tilt_deg || 0, 85));
    const cosf = Math.cos((tilt * Math.PI) / 180) || 1;
    const p = f.areaProj || 0;
    const r = p / cosf;
    const esclProj = (f.esclusioni || []).reduce((s, e) => s + (e.areaProj || 0), 0);
    const esclReale = esclProj / cosf;
    const marginLoss = (f.perimetro || 0) * (f.margin_m || 0);
    proj += p; reale += r; netta += Math.max(0, r - esclReale - marginLoss);
  });
  const moduli = moduleArea > 0 ? Math.floor(netta / moduleArea) : 0;
  return {
    proiettata_m2: proj, reale_inclinata_m2: reale, lorda_m2: reale, netta_m2: netta,
    moduli_max: moduli, potenza_kwp: +((moduli * (moduleWp || 0)) / 1000).toFixed(2),
  };
}

// Conversione lat/lng -> metri locali (est x, nord y) attorno al centro geolocalizzato.
function _toM(p, c) {
  const mLat = 111320, mLng = 111320 * Math.cos((c.lat * Math.PI) / 180);
  return { x: (p.lng - c.lng) * mLng, y: (p.lat - c.lat) * mLat };
}
// Area proiettata (m²) e perimetro (m) da un path lat/lng, via shoelace nei metri locali.
function areaPerimLL(pathLL, c) {
  if (!pathLL || pathLL.length < 2) return { area: 0, perim: 0 };
  const pts = pathLL.map((p) => _toM(p, c));
  let a = 0, per = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    a += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
    per += Math.hypot(pts[j].x - pts[i].x, pts[j].y - pts[i].y);
  }
  return { area: pts.length >= 3 ? Math.abs(a) / 2 : 0, perim: per };
}

// Tela di disegno in scala (griglia metrica). Produce gli STESSI dati delle falde disegnate su mappa:
// i vertici sono coordinate sintetiche attorno al centro geolocalizzato, così da qui in poi
// (generazione layout piano/inclinato, salvataggio) tutto funziona identico.
function GridDrawCanvas({ center, falde, mode, draft, setDraft, completeDraw, onInstant, selectedId, setSelectedId, annotations, updateFaldaPath, updateExclPath, updateAnnot, pdfImg = null, pdfMpp = 0.05, pdfImgW = 0, pdfImgH = 0, pdfOrigin = null, onCalibrate }) {
  const svgRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 460 });
  const [scale, setScale] = useState(16);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [drag, setDrag] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [ortho, setOrtho] = useState(true);
  const lpRef = useRef(null);
  const movedRef = useRef(false);
  const [calibMode, setCalibMode] = useState(false);
  const [calibPts, setCalibPts] = useState([]);

  const mLat = 111320, mLng = 111320 * Math.cos((center.lat * Math.PI) / 180);
  const toM = (p) => ({ x: (p.lng - center.lng) * mLng, y: (p.lat - center.lat) * mLat });
  const toLL = (x, y) => ({ lat: center.lat + y / mLat, lng: center.lng + x / mLng });
  const sx = (x) => size.w / 2 + pan.x + x * scale;
  const sy = (y) => size.h / 2 + pan.y - y * scale;
  const toScreen = (p) => { const m = toM(p); return { x: sx(m.x), y: sy(m.y) }; };
  const fromScreen = (px, py) => ({ x: (px - size.w / 2 - pan.x) / scale, y: (size.h / 2 + pan.y - py) / scale });
  const snap = (v) => Math.round(v * 10) / 10;
  // Snap ad angolo retto durante il disegno: allinea il nuovo lato a multipli di 90° rispetto al lato precedente (o agli assi).
  const orthoMeters = (m) => {
    if (!ortho || !m || draft.length < 1) return m;
    const prev = toM(draft[draft.length - 1]);
    let ref = 0;
    if (draft.length >= 2) { const pp = toM(draft[draft.length - 2]); ref = Math.atan2(prev.y - pp.y, prev.x - pp.x); }
    const vx = m.x - prev.x, vy = m.y - prev.y, len = Math.hypot(vx, vy);
    if (len < 0.05) return m;
    const ang = Math.atan2(vy, vx);
    let best = ang, bestd = Infinity;
    for (let k = 0; k < 4; k++) { const t = ref + k * Math.PI / 2; const d = Math.abs(Math.atan2(Math.sin(ang - t), Math.cos(ang - t))); if (d < bestd) { bestd = d; best = t; } }
    if (bestd <= 9 * Math.PI / 180) return { x: prev.x + len * Math.cos(best), y: prev.y + len * Math.sin(best) };
    return m;
  };
  // Allinea un vertice trascinato agli assi dei vicini (angoli retti su rettangoli allineati).
  const alignVertexLL = (path, pi, npt) => {
    if (!ortho || path.length < 3) return path.map((q, qi) => qi === pi ? npt : q);
    const n = path.length, prev = toM(path[(pi - 1 + n) % n]), next = toM(path[(pi + 1) % n]);
    const m = toM(npt), tol = 0.3;
    if (Math.abs(m.x - prev.x) < tol) m.x = prev.x; else if (Math.abs(m.x - next.x) < tol) m.x = next.x;
    if (Math.abs(m.y - prev.y) < tol) m.y = prev.y; else if (Math.abs(m.y - next.y) < tol) m.y = next.y;
    const adj = toLL(m.x, m.y);
    return path.map((q, qi) => qi === pi ? adj : q);
  };
  const insertVertex = (path, i) => { const n = path.length, j = (i + 1) % n, a = toM(path[i]), b = toM(path[j]); const np = path.slice(); np.splice(i + 1, 0, toLL((a.x + b.x) / 2, (a.y + b.y) / 2)); return np; };

  useEffect(() => {
    const el = svgRef.current; if (!el) return;
    const ro = new ResizeObserver(() => { const r = el.getBoundingClientRect(); setSize({ w: r.width, h: r.height }); });
    ro.observe(el);
    const r = el.getBoundingClientRect(); setSize({ w: r.width, h: r.height });
    return () => ro.disconnect();
  }, []);

  const rel = (e) => { const r = svgRef.current.getBoundingClientRect(); return { px: e.clientX - r.left, py: e.clientY - r.top }; };
  const minPts = mode?.kind === "cavidotto" ? 2 : 3;

  const onClick = (e) => {
    if (drag) return;
    if (calibMode) {
      const { px, py } = rel(e);
      const m = fromScreen(px, py); const wp = toLL(m.x, m.y);
      const next = [...calibPts, wp];
      if (next.length >= 2) {
        const a = toM(next[0]), b = toM(next[1]); const wd = Math.hypot(b.x - a.x, b.y - a.y);
        const v = window.prompt("Lunghezza reale del segmento (m)", "10");
        const L = parseFloat(String(v || "").replace(",", "."));
        if (isFinite(L) && L > 0 && wd > 0.001) onCalibrate?.(pdfMpp * L / wd);
        setCalibPts([]); setCalibMode(false);
      } else setCalibPts(next);
      return;
    }
    if (!mode) return;
    const { px, py } = rel(e);
    const mm = fromScreen(px, py);
    const o = orthoMeters(mm);
    const used = o !== mm ? o : { x: snap(mm.x), y: snap(mm.y) };
    const pt = toLL(used.x, used.y);
    if (mode.kind === "cabina" || mode.kind === "parcheggio") { onInstant(pt); return; }
    if (draft.length >= minPts) {
      const first = toScreen(draft[0]);
      if (Math.hypot(first.x - px, first.y - py) < 13) { completeDraw(); return; }
    }
    setDraft((d) => [...d, pt]);
  };
  const onPointerDown = (e) => {
    if (mode) return;
    try { svgRef.current.setPointerCapture?.(e.pointerId); } catch {}
    const { px, py } = rel(e);
    setDrag({ type: "pan", sx0: px, sy0: py, px0: pan.x, py0: pan.y });
  };
  const onPointerMove = (e) => {
    const { px, py } = rel(e);
    if (mode && draft.length) setCursor(fromScreen(px, py));
    if (!drag) return;
    if (drag.type === "pan") setPan({ x: drag.px0 + (px - drag.sx0), y: drag.py0 + (py - drag.sy0) });
    else if (drag.type === "vertex") {
      if (!movedRef.current && Math.hypot(px - drag.sx0, py - drag.sy0) > 6) { movedRef.current = true; if (lpRef.current) { clearTimeout(lpRef.current); lpRef.current = null; } }
      const m = fromScreen(px, py); drag.apply(toLL(snap(m.x), snap(m.y)));
    }
  };
  const onPointerUp = (e) => { if (lpRef.current) { clearTimeout(lpRef.current); lpRef.current = null; } try { svgRef.current.releasePointerCapture?.(e.pointerId); } catch {} setDrag(null); };
  const startVertex = (apply, onDelete) => (e) => {
    e.stopPropagation();
    try { svgRef.current.setPointerCapture?.(e.pointerId); } catch {}
    const { px, py } = rel(e);
    movedRef.current = false;
    if (onDelete) { lpRef.current = setTimeout(() => { if (!movedRef.current) { onDelete(); setDrag(null); } lpRef.current = null; }, 550); }
    setDrag({ type: "vertex", apply, sx0: px, sy0: py });
  };

  const setEdgeLen = (pathLL, i, newLen) => {
    const n = pathLL.length, j = (i + 1) % n;
    const a = toM(pathLL[i]), b = toM(pathLL[j]);
    const dx = b.x - a.x, dy = b.y - a.y, d = Math.hypot(dx, dy) || 1;
    const np = pathLL.slice();
    np[j] = toLL(a.x + (dx / d) * newLen, a.y + (dy / d) * newLen);
    return np;
  };
  const editEdge = (pathLL, i, onApply) => {
    const n = pathLL.length, j = (i + 1) % n;
    const cur = Math.hypot(toM(pathLL[j]).x - toM(pathLL[i]).x, toM(pathLL[j]).y - toM(pathLL[i]).y);
    const v = window.prompt("Lunghezza lato (m)", cur.toFixed(2).replace(".", ","));
    if (v == null) return;
    const len = parseFloat(String(v).replace(",", "."));
    if (!isFinite(len) || len <= 0) return;
    onApply(setEdgeLen(pathLL, i, len));
  };

  // Griglia
  const step = scale < 7 ? 5 : scale < 14 ? 2 : 1;
  const tl = fromScreen(0, 0), br = fromScreen(size.w, size.h);
  const gx0 = Math.floor(tl.x / step) * step, gx1 = Math.ceil(br.x / step) * step;
  const gy0 = Math.floor(br.y / step) * step, gy1 = Math.ceil(tl.y / step) * step;
  const gridLines = [];
  for (let x = gx0; x <= gx1; x += step) gridLines.push(<line key={`vx${x}`} x1={sx(x)} y1={0} x2={sx(x)} y2={size.h} stroke={x === 0 ? "#64748b" : "#e2e8f0"} strokeWidth={x === 0 ? 1.2 : 0.6} />);
  for (let y = gy0; y <= gy1; y += step) gridLines.push(<line key={`hy${y}`} x1={0} y1={sy(y)} x2={size.w} y2={sy(y)} stroke={y === 0 ? "#64748b" : "#e2e8f0"} strokeWidth={y === 0 ? 1.2 : 0.6} />);

  const ptsStr = (pathLL) => pathLL.map((p) => { const s = toScreen(p); return `${s.x},${s.y}`; }).join(" ");
  const edgeChips = (pathLL, color, onApply, onInsert, closed = true) => {
    const out = []; const n = pathLL.length; const lim = closed ? n : n - 1;
    for (let i = 0; i < lim; i++) {
      const j = (i + 1) % n;
      const a = toScreen(pathLL[i]), b = toScreen(pathLL[j]);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const dx = b.x - a.x, dy = b.y - a.y, dl = Math.hypot(dx, dy) || 1;
      const cxp = mid.x + (-dy / dl) * 17, cyp = mid.y + (dx / dl) * 17;
      const len = Math.hypot(toM(pathLL[j]).x - toM(pathLL[i]).x, toM(pathLL[j]).y - toM(pathLL[i]).y);
      out.push(
        <g key={`ch${i}`}>
          <g style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); editEdge(pathLL, i, onApply); }}>
            <rect x={cxp - 20} y={cyp - 9} width={40} height={18} rx={4} fill="#111" opacity={0.82} />
            <text x={cxp} y={cyp + 4} textAnchor="middle" fontSize="10" fill="#fff" fontWeight="600">{len.toFixed(2).replace(".", ",")}</text>
          </g>
          {onInsert && (
            <g style={{ cursor: "copy" }} onClick={(e) => { e.stopPropagation(); onInsert(i); }} data-testid={`grid-add-vertex-${i}`}>
              <circle cx={mid.x} cy={mid.y} r={8} fill="#22c55e" stroke="#fff" strokeWidth={1.5} />
              <text x={mid.x} y={mid.y + 3.5} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="800" pointerEvents="none">+</text>
            </g>
          )}
        </g>
      );
    }
    return out;
  };

  return (
    <div className="absolute inset-0 bg-white select-none" data-testid="grid-draw-canvas">
      <svg ref={svgRef} width="100%" height="100%" onClick={onClick} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp} style={{ cursor: mode ? "crosshair" : drag?.type === "pan" ? "grabbing" : "grab", display: "block", touchAction: "none" }}>
        {pdfImg && <image href={pdfImg} x={sx(toM(pdfOrigin || center).x)} y={sy(toM(pdfOrigin || center).y)} width={pdfImgW * pdfMpp * scale} height={pdfImgH * pdfMpp * scale} preserveAspectRatio="none" style={{ pointerEvents: "none" }} />}
        {!pdfImg && gridLines}
        {calibMode && calibPts.map((p, i) => { const s = toScreen(p); return <circle key={`cal${i}`} cx={s.x} cy={s.y} r={6} fill="#ef4444" stroke="#fff" strokeWidth={2} />; })}
        {/* Falde */}
        {falde.map((f, idx) => {
          const col = FALDA_COLORS[idx % FALDA_COLORS.length];
          const sel = f.id === selectedId;
          return (
            <g key={f.id}>
              <polygon points={ptsStr(f.path)} fill={col} fillOpacity={sel ? 0.28 : 0.16} stroke={col} strokeWidth={sel ? 3 : 2} style={{ cursor: "pointer" }} onClick={(e) => { if (mode) return; e.stopPropagation(); setSelectedId(f.id); }} />
              {(f.esclusioni || []).map((ex) => (
                <g key={ex.id}>
                  <polygon points={ptsStr(ex.path)} fill={EXCL_COLOR} fillOpacity={0.3} stroke={EXCL_COLOR} strokeWidth={1.5} />
                  {!mode && sel && ex.path.map((p, pi) => { const s = toScreen(p); const h = startVertex((np) => updateExclPath(f.id, ex.id, ex.path.map((q, qi) => qi === pi ? np : q)), ex.path.length > 3 ? () => updateExclPath(f.id, ex.id, ex.path.filter((_, qi) => qi !== pi)) : null); return <g key={pi}><circle cx={s.x} cy={s.y} r={16} fill="transparent" style={{ cursor: "move", touchAction: "none" }} onPointerDown={h} /><circle cx={s.x} cy={s.y} r={5} fill="#fff" stroke={EXCL_COLOR} strokeWidth={2} pointerEvents="none" /></g>; })}
                </g>
              ))}
              {!mode && sel && edgeChips(f.path, col, (np) => updateFaldaPath(f.id, np), (i) => updateFaldaPath(f.id, insertVertex(f.path, i)))}
              {!mode && sel && f.path.map((p, pi) => { const s = toScreen(p); const h = startVertex((npt) => updateFaldaPath(f.id, alignVertexLL(f.path, pi, npt)), f.path.length > 3 ? () => updateFaldaPath(f.id, f.path.filter((_, qi) => qi !== pi)) : null); return <g key={pi}><circle cx={s.x} cy={s.y} r={18} fill="transparent" style={{ cursor: "move", touchAction: "none" }} onPointerDown={h} /><circle cx={s.x} cy={s.y} r={7} fill="#fff" stroke={col} strokeWidth={2.5} pointerEvents="none" /></g>; })}
            </g>
          );
        })}
        {/* Annotazioni */}
        {annotations.map((a) => {
          const col = ANNOT[a.tipo]?.color || "#888";
          if (a.tipo === "cavidotto") return <polyline key={a.id} points={ptsStr(a.coordinates)} fill="none" stroke={col} strokeWidth={3} />;
          if (a.tipo === "parcheggio") { const s = toScreen(a.coordinates[0] || center); return <circle key={a.id} cx={s.x} cy={s.y} r={10} fill={col} stroke="#fff" strokeWidth={2} style={{ cursor: "move", touchAction: "none" }} onPointerDown={!mode ? startVertex((np) => updateAnnot(a.id, [np])) : undefined} />; }
          return (
            <g key={a.id}>
              <polygon points={ptsStr(a.coordinates)} fill={col} fillOpacity={0.3} stroke={col} strokeWidth={2} />
              {!mode && a.coordinates.map((p, pi) => { const s = toScreen(p); const h = startVertex((np) => updateAnnot(a.id, a.coordinates.map((q, qi) => qi === pi ? np : q)), a.coordinates.length > 3 ? () => updateAnnot(a.id, a.coordinates.filter((_, qi) => qi !== pi)) : null); return <g key={pi}><circle cx={s.x} cy={s.y} r={16} fill="transparent" style={{ cursor: "move", touchAction: "none" }} onPointerDown={h} /><circle cx={s.x} cy={s.y} r={5} fill="#fff" stroke={col} strokeWidth={2} pointerEvents="none" /></g>; })}
            </g>
          );
        })}
        {/* Disegno in corso */}
        {mode && draft.length > 0 && (() => {
          const cm = cursor ? orthoMeters(cursor) : null;
          const cl = cursor ? (cm !== cursor ? cm : { x: snap(cursor.x), y: snap(cursor.y) }) : null;
          const live = cursor ? [...draft, toLL(cl.x, cl.y)] : draft;
          const segs = [];
          for (let i = 0; i < live.length - 1; i++) {
            const a = toScreen(live[i]), b = toScreen(live[i + 1]);
            const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            const len = Math.hypot(toM(live[i + 1]).x - toM(live[i]).x, toM(live[i + 1]).y - toM(live[i]).y);
            segs.push(<g key={i}><line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#FDD003" strokeWidth={2.5} /><text x={mid.x} y={mid.y - 5} textAnchor="middle" fontSize="10" fill="#111" fontWeight="700">{len.toFixed(2).replace(".", ",")}</text></g>);
          }
          return <g>{segs}{draft.map((p, i) => { const s = toScreen(p); const first = i === 0 && draft.length >= minPts; return <circle key={i} cx={s.x} cy={s.y} r={first ? 8 : 5} fill={first ? "#22c55e" : "#fff"} stroke={first ? "#fff" : "#111"} strokeWidth={first ? 3 : 1.5} />; })}</g>;
        })()}
      </svg>
      {/* Area totale sempre visibile */}
      <div className="absolute top-2 left-2 bg-black/82 text-white rounded-md px-2.5 py-1 shadow pointer-events-none" data-testid="grid-total-area">
        <span className="text-[10px] uppercase tracking-wide opacity-80">Area totale</span>
        <div className="text-sm font-bold leading-tight">{falde.reduce((s, f) => s + (f.areaProj || 0), 0).toFixed(1).replace(".", ",")} m²</div>
      </div>
      {/* Bussola Nord + legenda scala */}
      <div className="absolute top-2 right-2 flex flex-col items-center gap-0.5 bg-white/90 rounded-md px-2 py-1 shadow border border-border pointer-events-none" data-testid="grid-north">
        <span className="text-[10px] font-bold leading-none text-foreground">N</span>
        <ArrowRight className="h-4 w-4 -rotate-90 text-foreground" strokeWidth={2.5} />
      </div>
      <div className="absolute bottom-2 left-2 z-10 flex items-center gap-1">
        <Button size="sm" variant={ortho ? "default" : "secondary"} className="h-7 px-2 shadow text-[11px]" onClick={() => setOrtho((v) => !v)} data-testid="grid-ortho-toggle" title="Snap ad angolo retto (90°)">⦜ 90° {ortho ? "ON" : "OFF"}</Button>
        {pdfImg && <Button size="sm" variant={calibMode ? "default" : "secondary"} className="h-7 px-2 shadow text-[11px]" onClick={() => { setCalibPts([]); setCalibMode((v) => !v); }} data-testid="grid-calibrate" title="Tara la scala: clicca 2 punti su una quota nota">{calibMode ? "Clicca 2 punti…" : "Tara scala (2 click)"}</Button>}
      </div>
      <div className="absolute bottom-2 right-2 flex items-center gap-1 z-10">
        <Button size="sm" variant="secondary" className="h-7 w-7 p-0 shadow" onClick={() => setScale((s) => Math.min(80, s * 1.25))} data-testid="grid-zoom-in">+</Button>
        <Button size="sm" variant="secondary" className="h-7 w-7 p-0 shadow" onClick={() => setScale((s) => Math.max(3, s / 1.25))} data-testid="grid-zoom-out">−</Button>
        <Button size="sm" variant="secondary" className="h-7 px-2 shadow text-[10px]" onClick={() => { setScale(16); setPan({ x: 0, y: 0 }); }} data-testid="grid-zoom-reset">Reset</Button>
        <span className="ml-1 text-[10px] bg-white/90 rounded px-1.5 py-0.5 border border-border text-muted-foreground">griglia {step} m</span>
      </div>
    </div>
  );
}

function MapController({ falde, setFalde, mode, setMode, draft, setDraft, selectedId, setSelectedId, onGeocode, geocodeReq, onCompleteDraw, annotations, setAnnotations, onInstant }) {
  const map = useMap();
  const geometry = useMapsLibrary("geometry");
  const places = useMapsLibrary("places");
  const polysRef = useRef({});
  const draftRef = useRef({ line: null, markers: [] });
  const completeRef = useRef(onCompleteDraw);
  completeRef.current = onCompleteDraw;
  const instantRef = useRef(onInstant);
  instantRef.current = onInstant;
  const annRef = useRef({});
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const draftStateRef = useRef(draft);
  draftStateRef.current = draft;
  const faldeRef = useRef(falde);
  faldeRef.current = falde;
  const didFitRef = useRef(false);

  // Al primo caricamento, se esistono già falde, centra/zooma la mappa su di esse
  // (come RoofMapView). Evita che la mappa resti sul center salvato (o su Milano di
  // default) quando l'utente aveva spostato la mappa a mano invece di usare la ricerca.
  useEffect(() => {
    if (!map || !window.google || didFitRef.current) return;
    didFitRef.current = true;
    const pts = [];
    (faldeRef.current || []).forEach((f) => (f.path || []).forEach((p) => pts.push(p)));
    if (pts.length < 3) return;
    const g = window.google;
    const bounds = new g.maps.LatLngBounds();
    pts.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 60);
    const l = g.maps.event.addListenerOnce(map, "idle", () => { if ((map.getZoom() || 0) > 21) map.setZoom(21); });
    return () => g.maps.event.removeListener(l);
  }, [map]);

  const areaOf = useCallback((path) => {
    if (!geometry || path.length < 3) return { area: 0, perim: 0 };
    const g = window.google;
    const latlngs = path.map((p) => new g.maps.LatLng(p.lat, p.lng));
    const area = geometry.spherical.computeArea(latlngs);
    const perim = geometry.spherical.computeLength([...latlngs, latlngs[0]]);
    return { area, perim };
  }, [geometry]);

  // Geocoding richiesta indirizzo
  useEffect(() => {
    if (!geocodeReq || !map || !window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: geocodeReq }, (res, status) => {
      if (status === "OK" && res[0]) {
        const loc = res[0].geometry.location;
        map.panTo(loc); map.setZoom(20);
        onGeocode({ lat: loc.lat(), lng: loc.lng() }, res[0].formatted_address);
      } else {
        toast.error("Indirizzo non trovato");
        onGeocode(null, null);
      }
    });
  }, [geocodeReq, map, onGeocode]);

  // Click sulla mappa in modalità disegno (chiusura automatica cliccando vicino al primo punto)
  useEffect(() => {
    if (!map) return;
    const l = map.addListener("click", (e) => {
      const m = modeRef.current;
      if (!m) return;
      const pt = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      const kind = m.kind || (m.tipo ? "ostacolo" : "falda");
      if (kind === "cabina" || kind === "parcheggio") { instantRef.current && instantRef.current(pt); return; }
      const isPolygon = (kind === "falda" || kind === "ostacolo" || kind === "locale");
      const cur = draftStateRef.current || [];
      if (isPolygon && cur.length >= 3 && geometry && window.google) {
        const first = cur[0];
        const z = map.getZoom() || 20;
        const mpp = (156543.03392 * Math.cos((first.lat * Math.PI) / 180)) / Math.pow(2, z);
        const dist = geometry.spherical.computeDistanceBetween(
          new window.google.maps.LatLng(pt.lat, pt.lng),
          new window.google.maps.LatLng(first.lat, first.lng)
        );
        // Clic entro ~18px dal primo punto => chiudo il poligono senza premere "Completa"
        if (dist <= mpp * 18) { completeRef.current && completeRef.current(); return; }
      }
      setDraft((d) => [...d, pt]);
    });
    return () => window.google?.maps?.event?.removeListener(l);
  }, [map, geometry, setDraft]);

  // Render del disegno in corso (polilinea + vertici)
  useEffect(() => {
    if (!map || !window.google) return;
    const g = window.google;
    draftRef.current.markers.forEach((m) => m.setMap(null));
    draftRef.current.markers = [];
    if (draftRef.current.line) draftRef.current.line.setMap(null);
    if (draft.length) {
      // Anteprima APERTA: la polilinea segue i punti senza collegare l'ultimo al primo,
      // così il poligono si "chiude" visivamente solo quando si torna sul punto d'origine.
      draftRef.current.line = new g.maps.Polyline({
        path: draft, map, strokeColor: (mode?.kind && ANNOT[mode.kind === "locale" ? "locale_tecnico" : mode.kind]?.color) || (mode?.tipo ? EXCL_COLOR : "#FDD003"), strokeWeight: 2,
      });
      draftRef.current.markers = draft.map((p, i) => {
        const canClose = i === 0 && draft.length >= 3;
        const marker = new g.maps.Marker({
          position: p, map,
          cursor: canClose ? "pointer" : undefined,
          title: canClose ? "Clicca qui per chiudere la falda" : undefined,
          zIndex: canClose ? 10 : 1,
          icon: canClose
            ? { path: g.maps.SymbolPath.CIRCLE, scale: 9, fillColor: "#22c55e", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3 }
            : { path: g.maps.SymbolPath.CIRCLE, scale: 5, fillColor: "#fff", fillOpacity: 1, strokeColor: "#111", strokeWeight: 1 },
        });
        if (canClose) marker.addListener("click", () => completeRef.current && completeRef.current());
        return marker;
      });
    }
  }, [draft, map, mode]);

  // Sincronizza poligoni falde (crea quelli nuovi, rimuove gli assenti)
  useEffect(() => {
    if (!map || !window.google || !geometry) return;
    const g = window.google;
    const drawing = !!mode;
    const ids = new Set(falde.map((f) => f.id));
    // rimuovi poligoni orfani
    Object.keys(polysRef.current).forEach((id) => {
      if (!ids.has(id)) {
        const entry = polysRef.current[id];
        entry.poly?.setMap(null);
        (entry.excl || []).forEach((x) => x.poly.setMap(null));
        delete polysRef.current[id];
      }
    });
    falde.forEach((f, idx) => {
      let entry = polysRef.current[f.id];
      const color = FALDA_COLORS[idx % FALDA_COLORS.length];
      if (!entry) {
        const poly = new g.maps.Polygon({
          paths: f.path, map, editable: !drawing, draggable: !drawing, clickable: !drawing,
          strokeColor: color, strokeWeight: 2, fillColor: color, fillOpacity: 0.25, zIndex: 5,
        });
        const sync = () => {
          const path = poly.getPath().getArray().map((p) => ({ lat: p.lat(), lng: p.lng() }));
          const { area, perim } = areaOf(path);
          setFalde((arr) => arr.map((x) => (x.id === f.id ? { ...x, path, areaProj: area, perimetro: perim } : x)));
        };
        ["set_at", "insert_at", "remove_at"].forEach((ev) => poly.getPath().addListener(ev, sync));
        poly.addListener("dragend", sync);
        poly.addListener("click", () => setSelectedId(f.id));
        poly.addListener("rightclick", (e) => {
          if (e.vertex != null && poly.getPath().getLength() > 3) poly.getPath().removeAt(e.vertex);
        });
        entry = { poly, excl: [] };
        polysRef.current[f.id] = entry;
        sync();
      } else {
        entry.poly.setOptions({ strokeColor: color, fillColor: color, strokeWeight: f.id === selectedId ? 4 : 2, editable: !drawing, draggable: !drawing, clickable: !drawing });
      }
      // esclusioni
      const exIds = new Set((f.esclusioni || []).map((e) => e.id));
      entry.excl = (entry.excl || []).filter((x) => { if (!exIds.has(x.id)) { x.poly.setMap(null); return false; } return true; });
      (f.esclusioni || []).forEach((ex) => {
        if (entry.excl.find((x) => x.id === ex.id)) return;
        const poly = new g.maps.Polygon({
          paths: ex.path, map, editable: !drawing, draggable: !drawing, clickable: !drawing,
          strokeColor: EXCL_COLOR, strokeWeight: 2, fillColor: EXCL_COLOR, fillOpacity: 0.35, zIndex: 6,
        });
        const sync = () => {
          const path = poly.getPath().getArray().map((p) => ({ lat: p.lat(), lng: p.lng() }));
          const { area } = areaOf(path);
          setFalde((arr) => arr.map((x) => x.id === f.id ? { ...x, esclusioni: x.esclusioni.map((e) => e.id === ex.id ? { ...e, path, areaProj: area } : e) } : x));
        };
        ["set_at", "insert_at", "remove_at"].forEach((evn) => poly.getPath().addListener(evn, sync));
        poly.addListener("dragend", sync);
        poly.addListener("rightclick", (e) => { if (e.vertex != null && poly.getPath().getLength() > 3) poly.getPath().removeAt(e.vertex); });
        entry.excl.push({ id: ex.id, poly });
        sync();
      });
      entry.excl.forEach((x) => x.poly.setOptions({ editable: !drawing, draggable: !drawing, clickable: !drawing }));
    });
  }, [falde, map, geometry, areaOf, setFalde, selectedId, setSelectedId, mode]);

  // Annotazioni informative (cabina, cavidotti, locale tecnico, parcheggio): editabili e trascinabili
  useEffect(() => {
    if (!map || !window.google) return;
    const g = window.google;
    const ids = new Set((annotations || []).map((a) => a.id));
    Object.keys(annRef.current).forEach((id) => {
      if (!ids.has(id)) { annRef.current[id].forEach((o) => o.setMap(null)); delete annRef.current[id]; }
    });
    (annotations || []).forEach((a) => {
      if (annRef.current[a.id]) return;
      const meta = ANNOT[a.tipo]; if (!meta) return;
      const path = (a.coordinates || []).map((c) => ({ lat: c.lat, lng: c.lng }));
      if (!path.length) return;
      const overlays = [];
      const syncPath = (getArr) => { const p = getArr().map((q) => ({ lat: q.lat(), lng: q.lng() })); setAnnotations((arr) => arr.map((x) => x.id === a.id ? { ...x, coordinates: p } : x)); };
      if (a.tipo === "cabina" || a.tipo === "locale_tecnico") {
        const poly = new g.maps.Polygon({ paths: path, map, editable: true, draggable: true, strokeColor: meta.color, strokeWeight: 2, fillColor: meta.color, fillOpacity: 0.3, zIndex: 7 });
        ["set_at", "insert_at", "remove_at"].forEach((ev) => poly.getPath().addListener(ev, () => syncPath(() => poly.getPath().getArray())));
        poly.addListener("dragend", () => syncPath(() => poly.getPath().getArray()));
        overlays.push(poly);
      } else if (a.tipo === "cavidotto") {
        const pl2 = new g.maps.Polyline({ path, map, editable: true, draggable: true, strokeColor: meta.color, strokeWeight: 4, zIndex: 7 });
        ["set_at", "insert_at", "remove_at"].forEach((ev) => pl2.getPath().addListener(ev, () => syncPath(() => pl2.getPath().getArray())));
        pl2.addListener("dragend", () => syncPath(() => pl2.getPath().getArray()));
        overlays.push(pl2);
      } else if (a.tipo === "parcheggio") {
        const mk = new g.maps.Marker({ position: path[0], map, draggable: true, zIndex: 8, icon: { path: g.maps.SymbolPath.CIRCLE, scale: 8, fillColor: meta.color, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 } });
        mk.addListener("dragend", (e) => { const p = { lat: e.latLng.lat(), lng: e.latLng.lng() }; setAnnotations((arr) => arr.map((x) => x.id === a.id ? { ...x, coordinates: [p] } : x)); });
        overlays.push(mk);
      }
      overlays.push(new g.maps.Marker({ position: path[0], map, clickable: false, zIndex: 9, icon: { path: g.maps.SymbolPath.CIRCLE, scale: 0.01, fillOpacity: 0, strokeOpacity: 0 }, label: { text: a.label || meta.label, color: meta.color, fontWeight: "bold", fontSize: "12px" } }));
      annRef.current[a.id] = overlays;
    });
  }, [annotations, map, setAnnotations]);

  return null;
}

function RoofInner({ lead, initialRoof, moduleInfo, onSaved, selfToken, roofType, setRoofType, stepped = false, step = 3, setStep, rete = false }) {
  const [falde, setFalde] = useState(() => (initialRoof?.falde || []).map((f) => ({
    id: f.id || uid(), path: f.coordinates || [], tilt_deg: f.tilt_deg ?? 12, azimuth_deg: f.azimuth_deg ?? 180,
    margin_m: f.margin_m ?? 0.3, areaProj: f.proiettata_m2 || 0, perimetro: f.perimetro_m || 0,
    disposizione: f.disposizione || "auto",
    copertura: (f.copertura === "inclinato" || f.copertura === "piano") ? f.copertura : null,
    esclusioni: (f.esclusioni || []).map((e) => ({ id: uid(), tipo: e.tipo, path: e.coordinates || [], areaProj: e.proiettata_m2 || 0 })),
  })));
  const [mode, setMode] = useState(null); // null | {tipo?} ; tipo => esclusione
  const [draft, setDraft] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [address, setAddress] = useState(initialRoof?.address || cantiereAddress(lead) || "");
  const [geocodeReq, setGeocodeReq] = useState(null);
  const [pdfBg, setPdfBg] = useState(() => {
    const b = initialRoof?.pdf_bg || null;
    return b && !b.origin ? { ...b, origin: initialRoof?.center || { lat: 45.4642, lng: 9.19 } } : b;
  });
  const [pdfImg, setPdfImg] = useState(null);
  const pdfInputRef = useRef(null);
  const pdfFileRef = useRef(null);
  const canPdf = selfToken ? rete : (!!lead.id || (typeof window !== "undefined" && !!localStorage.getItem("token")));
  const [center, setCenter] = useState(initialRoof?.center || { lat: 45.4642, lng: 9.19 });
  const [saving, setSaving] = useState(false);
  const [annotations, setAnnotations] = useState(() => (initialRoof?.annotations || []).map((a) => ({ id: a.id || uid(), tipo: a.tipo, label: a.label || (ANNOT[a.tipo]?.label), size: a.size || null, coordinates: a.coordinates || [] })));
  const modeR = useRef(mode); modeR.current = mode;
  const [drawMode, setDrawMode] = useState(!!(initialRoof?.pdf_bg || initialRoof?.draw_mode));
  const updateFaldaPath = useCallback((id, path) => {
    const { area, perim } = areaPerimLL(path, center);
    setFalde((arr) => arr.map((f) => f.id === id ? { ...f, path, areaProj: area, perimetro: perim } : f));
  }, [center]);
  const updateExclPath = useCallback((fid, eid, path) => {
    const { area } = areaPerimLL(path, center);
    setFalde((arr) => arr.map((f) => f.id === fid ? { ...f, esclusioni: f.esclusioni.map((e) => e.id === eid ? { ...e, path, areaProj: area } : e) } : f));
  }, [center]);
  const updateAnnot = useCallback((id, coordinates) => setAnnotations((arr) => arr.map((a) => a.id === id ? { ...a, coordinates } : a)), []);
  const addAnnotation = (a) => setAnnotations((arr) => [...arr, { id: uid(), label: ANNOT[a.tipo]?.label, size: null, ...a }]);
  const removeAnnotation = (id) => setAnnotations((arr) => arr.filter((x) => x.id !== id));
  const startCabina = (size) => { setMode({ kind: "cabina", size }); setDraft([]); };
  const startCavidotto = () => { setMode({ kind: "cavidotto" }); setDraft([]); };
  const startLocale = () => { setMode({ kind: "locale" }); setDraft([]); };
  const startParcheggio = () => { setMode({ kind: "parcheggio" }); setDraft([]); };
  const onInstant = useCallback((pt) => {
    const m = modeR.current; if (!m) return;
    if (m.kind === "parcheggio") addAnnotation({ tipo: "parcheggio", coordinates: [pt] });
    else if (m.kind === "cabina") {
      const [w, h] = String(m.size || "10x3").split("x").map(Number);
      const dLat = (h / 2) / 111320, dLng = (w / 2) / (111320 * Math.cos((pt.lat * Math.PI) / 180));
      addAnnotation({ tipo: "cabina", size: m.size, coordinates: [
        { lat: pt.lat - dLat, lng: pt.lng - dLng }, { lat: pt.lat - dLat, lng: pt.lng + dLng },
        { lat: pt.lat + dLat, lng: pt.lng + dLng }, { lat: pt.lat + dLat, lng: pt.lng - dLng }] });
    }
    setMode(null); setDraft([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al primo caricamento centra la mappa satellitare sull'indirizzo (via + comune + provincia).
  // Evita che l'editor si apra sul default (Milano) quando non ci sono ancora falde disegnate.
  const didAutoGeocode = useRef(false);
  useEffect(() => {
    if (didAutoGeocode.current) return;
    didAutoGeocode.current = true;
    if (falde.length > 0) return; // roof esistente: MapController centra sulle falde
    const q = [lead?.address, lead?.city, lead?.provincia].filter((x) => (x || "").trim()).join(", ").trim() || address;
    if (q) setGeocodeReq(/italia|italy/i.test(q) ? q : `${q}, Italia`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moduleArea = moduleInfo?.area_m2 || 2.2;
  const moduleWp = moduleInfo?.potenza_wp || 0;
  const summary = clientSummary(falde, moduleArea, moduleWp);

  const startFalda = () => { setMode({}); setDraft([]); };
  const startExcl = () => { if (!selectedId) { toast.message("Seleziona prima una falda"); return; } setMode({ tipo: "ostacolo", faldaId: selectedId }); setDraft([]); };
  const cancelDraw = () => { setMode(null); setDraft([]); };

  const completeDraw = () => {
    const kind = mode?.kind || (mode?.tipo ? "ostacolo" : "falda");
    if (kind === "cavidotto") {
      if (draft.length < 2) { toast.error("Servono almeno 2 punti"); return; }
      addAnnotation({ tipo: "cavidotto", coordinates: draft });
    } else if (kind === "locale") {
      if (draft.length < 3) { toast.error("Servono almeno 3 punti"); return; }
      addAnnotation({ tipo: "locale_tecnico", coordinates: draft });
    } else if (kind === "ostacolo") {
      if (draft.length < 3) { toast.error("Servono almeno 3 punti"); return; }
      const { area } = areaPerimLL(draft, center);
      setFalde((arr) => arr.map((f) => f.id === mode.faldaId ? { ...f, esclusioni: [...(f.esclusioni || []), { id: uid(), tipo: mode.tipo, path: draft, areaProj: area }] } : f));
    } else {
      if (draft.length < 3) { toast.error("Servono almeno 3 punti"); return; }
      const id = uid();
      const { area, perim } = areaPerimLL(draft, center);
      setFalde((arr) => [...arr, { id, path: draft, tilt_deg: 12, azimuth_deg: 180, margin_m: 0.3, disposizione: "auto", copertura: null, areaProj: area, perimetro: perim, esclusioni: [] }]);
      setSelectedId(id);
    }
    setMode(null); setDraft([]);
  };

  const removeFalda = (id) => { setFalde((arr) => arr.filter((f) => f.id !== id)); if (selectedId === id) setSelectedId(null); };
  const removeExcl = (fid, eid) => setFalde((arr) => arr.map((f) => f.id === fid ? { ...f, esclusioni: f.esclusioni.filter((e) => e.id !== eid) } : f));
  const setFaldaField = (id, k, v) => setFalde((arr) => arr.map((f) => f.id === id ? { ...f, [k]: v } : f));
  const setFaldaCopertura = (id, v) => { setFaldaField(id, "copertura", v); toast.message("Copertura aggiornata: salva la misurazione e rigenera i layout per aggiornare i pannelli."); };

  const onGeocode = useCallback((coords, formatted) => {
    setGeocodeReq(null);
    if (coords) { setCenter(coords); if (formatted) setAddress(formatted); }
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Center = centroide delle falde disegnate (se presenti), così la mappa in
      // visualizzazione/editor si apre SEMPRE sulle falde reali e non sul geocode città.
      const allPts = [];
      falde.forEach((f) => (f.path || []).forEach((p) => allPts.push(p)));
      const savedCenter = allPts.length >= 3
        ? { lat: allPts.reduce((s, p) => s + p.lat, 0) / allPts.length, lng: allPts.reduce((s, p) => s + p.lng, 0) / allPts.length }
        : center;
      const payload = {
        address, center: savedCenter,
        falde: falde.map((f) => ({
          id: f.id, coordinates: f.path, proiettata_m2: f.areaProj || 0, perimetro_m: f.perimetro || 0,
          tilt_deg: Number(f.tilt_deg) || 0, azimuth_deg: Number(f.azimuth_deg) || 0, margin_m: Number(f.margin_m) || 0,
          disposizione: f.disposizione || "auto",
          copertura: (f.copertura === "inclinato" || f.copertura === "piano") ? f.copertura : null,
          esclusioni: (f.esclusioni || []).map((e) => ({ tipo: e.tipo, coordinates: e.path, proiettata_m2: e.areaProj || 0 })),
        })),
        annotations: annotations.map((a) => ({ id: a.id, tipo: a.tipo, label: a.label, size: a.size || null, coordinates: a.coordinates })),
        roof_type: roofType || "inclinato",
        pdf_bg: pdfBg || null,
        draw_mode: drawMode,
      };
      const { data } = await api.put(selfToken ? `/public/self/${selfToken}/roof` : `/leads/${lead.id}/roof`, payload);
      onSaved?.(data);
      toast.success("Misurazione tetto salvata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const [isFullscreen, setIsFullscreen] = useState(false);
  const renderPdf = async (src, page) => {
    const pdfjs = await import("pdfjs-dist");
    pdfjs.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.worker.min.mjs";
    const doc = await pdfjs.getDocument(src).promise;
    const pg = await doc.getPage(Math.min(Math.max(page || 1, 1), doc.numPages));
    const vp = pg.getViewport({ scale: 2 });
    const cv = document.createElement("canvas"); cv.width = vp.width; cv.height = vp.height;
    await pg.render({ canvasContext: cv.getContext("2d"), viewport: vp }).promise;
    return { dataUrl: cv.toDataURL("image/jpeg", 0.85), w: vp.width, h: vp.height, numPages: doc.numPages };
  };
  const onPickPdf = async (file) => {
    if (!file) return;
    const t = toast.loading("Caricamento e analisi PDF…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const ep = selfToken ? `/public/self/${selfToken}/roof-pdf` : lead.id ? `/leads/${lead.id}/roof-pdf` : `/roof-pdf`;
      const { data } = await api.post(ep, fd);
      pdfFileRef.current = file;
      const r = await renderPdf(URL.createObjectURL(file), 1);
      setPdfImg(r.dataUrl);
      setPdfBg({ storage_path: data.storage_path, page: 1, mpp: 15 / r.w, imgW: r.w, imgH: r.h, num_pages: r.numPages, origin: center });
      setDrawMode(true);
      toast.dismiss(t); toast.success("PDF caricato. Ora tara la scala con 2 click.");
    } catch (e) { toast.dismiss(t); toast.error("Errore nel caricamento del PDF"); }
    finally { if (pdfInputRef.current) pdfInputRef.current.value = ""; }
  };
  const changePdfPage = async (page) => {
    if (!pdfBg?.storage_path) return;
    try {
      const src = pdfFileRef.current || (await api.get(`/files/${pdfBg.storage_path}`, { responseType: "blob" })).data;
      const r = await renderPdf(URL.createObjectURL(src), page);
      setPdfImg(r.dataUrl);
      setPdfBg((b) => ({ ...b, page, imgW: r.w, imgH: r.h, num_pages: r.numPages }));
    } catch (e) { toast.error("Errore nel cambio pagina"); }
  };
  useEffect(() => {
    if (pdfBg?.storage_path && !pdfImg) {
      if (pdfBg.png_token) { setPdfImg(roofBgUrl(pdfBg.png_token)); return; }
      (async () => {
        try { const blob = await api.get(`/files/${pdfBg.storage_path}`, { responseType: "blob" }); const r = await renderPdf(URL.createObjectURL(blob.data), pdfBg.page || 1); setPdfImg(r.dataUrl); } catch {}
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfBg?.storage_path]);
  const rootRef = useRef(null);
  const toggleFullscreen = useCallback(() => setIsFullscreen((v) => !v), []);
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setIsFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFullscreen]);

  return (
    <div ref={rootRef} className={isFullscreen ? "fixed inset-0 z-[60] bg-background overflow-auto p-4 space-y-4" : "space-y-4"} data-testid="roof-editor-root">
      {/* STEP unico — Il tuo tetto: tipologia + indirizzo + mappa + falde */}
      {(!stepped || step === 3) && (
      <div className="space-y-4">
      {stepped && (
        <Button variant="outline" size="sm" onClick={() => setStep(1)} data-testid="wizard-back-3"><ArrowLeft className="h-4 w-4 mr-1.5" /> Indietro: pannello</Button>
      )}
      {/* Tipologia copertura (evidenziata, lampeggio lieve) */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border-2 border-primary/70 bg-primary/5 px-3 py-2.5 animate-soft-blink" data-testid="roof-type-selector">
        <span className="text-xs uppercase tracking-widest text-primary font-bold mr-1">Che tetto hai? Scegli qui →</span>
        <Button size="sm" variant={roofType !== "piano" ? "default" : "outline"} onClick={() => setRoofType("inclinato")} data-testid="roof-type-inclinato">Tetto inclinato</Button>
        <Button size="sm" variant={roofType === "piano" ? "default" : "outline"} onClick={() => setRoofType("piano")} data-testid="roof-type-piano">Tetto piano</Button>
        {roofType === "piano" && <span className="text-[11px] text-muted-foreground ml-1">Sistema TORRI PRSG · moduli landscape · inclinazione e file gestite qui sotto.</span>}
      </div>
      {/* Barra ricerca */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={address} onChange={(e) => setAddress(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setGeocodeReq(address)} placeholder="Indirizzo del cantiere (via, civico, città)" data-testid="roof-address-input" />
        <Button variant="outline" onClick={() => setGeocodeReq(address)} data-testid="roof-search-btn" className="shrink-0"><Search className="h-4 w-4 mr-1.5" /> Cerca</Button>
        <Button variant="outline" onClick={toggleFullscreen} data-testid="roof-fullscreen-btn" className="shrink-0" title={isFullscreen ? "Esci da schermo intero (Esc)" : "Schermo intero"}>
          {isFullscreen ? <Minimize2 className="h-4 w-4 mr-1.5" /> : <Maximize2 className="h-4 w-4 mr-1.5" />}
          {isFullscreen ? "Esci" : "Schermo intero"}
        </Button>
        <Button variant={drawMode ? "default" : "outline"} onClick={() => { setMode(null); setDraft([]); setDrawMode((v) => !v); }} data-testid="roof-drawmode-toggle" className="shrink-0" title="Disegna il tetto in scala su griglia, senza satellite">
          <Grid3x3 className="h-4 w-4 mr-1.5" /> {drawMode ? "Modalità disegno ON" : "Attiva modalità disegno"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Mappa */}
        <div className="lg:col-span-2 rounded-lg overflow-hidden border border-border relative" style={{ height: isFullscreen ? "calc(100vh - 230px)" : 460 }} data-testid="roof-map">
          {drawMode ? (
            <GridDrawCanvas center={center} falde={falde} mode={mode} draft={draft} setDraft={setDraft} completeDraw={completeDraw} onInstant={onInstant} selectedId={selectedId} setSelectedId={setSelectedId} annotations={annotations} updateFaldaPath={updateFaldaPath} updateExclPath={updateExclPath} updateAnnot={updateAnnot} pdfImg={pdfImg} pdfMpp={pdfBg?.mpp || 0.05} pdfImgW={pdfBg?.imgW || 0} pdfImgH={pdfBg?.imgH || 0} pdfOrigin={pdfBg?.origin || center} onCalibrate={(m) => setPdfBg((b) => ({ ...(b || {}), mpp: m }))} />
          ) : (
          <Map reuseMaps renderingType="RASTER" defaultCenter={center} center={undefined} defaultZoom={19} mapTypeId="hybrid" gestureHandling="greedy" disableDefaultUI={false} mapTypeControl={false} streetViewControl={false} fullscreenControl={false} tilt={0} style={{ width: "100%", height: "100%" }}>
            <MapController falde={falde} setFalde={setFalde} mode={mode} setMode={setMode} draft={draft} setDraft={setDraft} selectedId={selectedId} setSelectedId={setSelectedId} onGeocode={onGeocode} geocodeReq={geocodeReq} onCompleteDraw={completeDraw} annotations={annotations} setAnnotations={setAnnotations} onInstant={onInstant} />
          </Map>
          )}
          {/* Toolbar disegno */}
          <div className="absolute top-2 left-2 flex flex-wrap gap-1.5 z-10">
            {!mode ? (
              <>
                <Button size="default" onClick={startFalda} data-testid="roof-add-falda" className={`font-bold shadow-lg text-sm px-4 ring-2 ring-white/80 ring-offset-2 ring-offset-black/50 ${falde.length === 0 ? "animate-soft-blink" : ""}`}><Plus className="h-5 w-5 mr-1.5" strokeWidth={3} /> Seleziona area</Button>
                <Button size="sm" variant="secondary" onClick={startExcl} data-testid="roof-add-excl" className="shadow"><TriangleAlert className="h-3.5 w-3.5 mr-1" /> Ostacolo</Button>
                {drawMode && canPdf && (
                  <>
                    <input ref={pdfInputRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => onPickPdf(e.target.files?.[0])} data-testid="roof-pdf-input" />
                    <Button size="sm" variant="secondary" onClick={() => pdfInputRef.current?.click()} data-testid="roof-pdf-upload" className="shadow"><FileUp className="h-3.5 w-3.5 mr-1" /> Carica PDF quotato</Button>
                    {pdfBg?.num_pages > 1 && (
                      <select value={pdfBg.page} onChange={(e) => changePdfPage(Number(e.target.value))} className="h-9 rounded-md border border-border bg-background px-2 text-sm shadow" data-testid="roof-pdf-page">
                        {Array.from({ length: pdfBg.num_pages }, (_, i) => <option key={i} value={i + 1}>Pag. {i + 1}</option>)}
                      </select>
                    )}
                    {pdfBg && <Button size="sm" variant="secondary" onClick={() => { setPdfBg(null); setPdfImg(null); pdfFileRef.current = null; }} data-testid="roof-pdf-remove" className="shadow text-destructive"><X className="h-3.5 w-3.5 mr-1" /> Rimuovi PDF</Button>}
                  </>
                )}
                {!selfToken && (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => startCabina("10x3")} data-testid="roof-add-cabina-10x3" className="shadow" style={{ borderLeft: `3px solid ${ANNOT.cabina.color}` }}>Cabina 10×3</Button>
                    <Button size="sm" variant="secondary" onClick={() => startCabina("15x3")} data-testid="roof-add-cabina-15x3" className="shadow" style={{ borderLeft: `3px solid ${ANNOT.cabina.color}` }}>15×3</Button>
                    <Button size="sm" variant="secondary" onClick={() => startCabina("20x3")} data-testid="roof-add-cabina-20x3" className="shadow" style={{ borderLeft: `3px solid ${ANNOT.cabina.color}` }}>20×3</Button>
                    <Button size="sm" variant="secondary" onClick={startCavidotto} data-testid="roof-add-cavidotto" className="shadow" style={{ borderLeft: `3px solid ${ANNOT.cavidotto.color}` }}>Cavidotti</Button>
                    <Button size="sm" variant="secondary" onClick={startLocale} data-testid="roof-add-locale" className="shadow" style={{ borderLeft: `3px solid ${ANNOT.locale_tecnico.color}` }}>Locale tecnico</Button>
                    <Button size="sm" variant="secondary" onClick={startParcheggio} data-testid="roof-add-parcheggio" className="shadow" style={{ borderLeft: `3px solid ${ANNOT.parcheggio.color}` }}>Parcheggio</Button>
                  </>
                )}
              </>
            ) : (
              <>
                {(mode.kind === "cavidotto" || mode.kind === "locale" || !mode.kind || mode.tipo) && (
                  <Button size="sm" onClick={completeDraw} data-testid="roof-complete" className="font-semibold shadow bg-green-600 hover:bg-green-700"><Check className="h-3.5 w-3.5 mr-1" /> Completa ({draft.length})</Button>
                )}
                <Button size="sm" variant="secondary" onClick={cancelDraw} className="shadow"><X className="h-3.5 w-3.5 mr-1" /> Annulla</Button>
              </>
            )}
          </div>
          {mode && (
            <div className="absolute bottom-2 left-2 right-2 bg-black/70 text-white text-xs rounded px-3 py-2 z-10" data-testid="roof-draw-hint">
              {mode.kind === "cabina" ? <>Clicca sulla mappa <b>dove posizionare la cabina</b> ({(mode.size || "10x3").replace("x", "×")} m). Poi puoi trascinarla e ridimensionarla dai vertici.</>
                : mode.kind === "parcheggio" ? <>Clicca sulla mappa il <b>punto di parcheggio dei mezzi di sollevamento</b>. Poi puoi trascinarlo.</>
                : mode.kind === "cavidotto" ? <>Clicca i punti del percorso <b>cavidotti</b> e premi "Completa" (min. 2 punti). Sono semplici linee.</>
                : mode.kind === "locale" ? <>Clicca i vertici del <b>locale tecnico/inverter</b>; torna vicino al primo punto o premi "Completa" (min. 3 punti).</>
                : <>Clicca sulla mappa per aggiungere i vertici {mode.tipo ? "dell'ostacolo" : "della falda"}. Con almeno 3 punti, clicca <b>vicino al primo punto (verde)</b> per chiudere, oppure premi "Completa". Dopo puoi trascinare i vertici; tasto destro su un vertice per eliminarlo.</>}
            </div>
          )}
        </div>

        {/* Pannello falde */}
        <div className={isFullscreen ? "space-y-3 overflow-y-auto pr-1" : "space-y-3"} style={isFullscreen ? { maxHeight: "calc(100vh - 230px)" } : undefined}>
          <div className="rounded-lg border border-border bg-secondary/20 p-3" data-testid="roof-summary">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-2 flex items-center gap-1.5"><Ruler className="h-3.5 w-3.5" /> Superfici & potenza</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <SummaryRow label="Proiettata" value={`${num(summary.proiettata_m2)} m²`} />
              <SummaryRow label="Reale inclinata" value={`${num(summary.reale_inclinata_m2)} m²`} />
              <SummaryRow label="Lorda" value={`${num(summary.lorda_m2)} m²`} />
              <SummaryRow label="Netta utilizzabile" value={`${num(summary.netta_m2)} m²`} accent />
              {!selfToken && <SummaryRow label="Moduli max" value={summary.moduli_max} accent testid="roof-moduli" />}
              {!selfToken && <SummaryRow label="Potenza installabile" value={`${num(summary.potenza_kwp)} kWp`} accent testid="roof-kwp" />}
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">Modulo {moduleInfo?.model || "—"}: {num(moduleArea)} m²{moduleWp ? ` · ${moduleWp} Wp` : ""}. Stima separata (non modifica il dimensionamento consumi).</p>
          </div>

          {falde.length === 0 && <p className="text-xs text-muted-foreground px-1">Nessuna falda disegnata. Premi "Falda" e traccia il perimetro sul tetto.</p>}

          {falde.map((f, idx) => (
            <div key={f.id} className={`rounded-lg border p-3 cursor-pointer ${f.id === selectedId ? "border-primary" : "border-border"}`} onClick={() => setSelectedId(f.id)} data-testid={`roof-falda-${idx}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold flex items-center gap-1.5">
                  <span className="inline-block h-3 w-3 rounded-sm" style={{ background: FALDA_COLORS[idx % FALDA_COLORS.length] }} /> Falda {idx + 1}
                </span>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={(e) => { e.stopPropagation(); removeFalda(f.id); }} data-testid={`roof-falda-del-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="text-[11px] text-muted-foreground mb-2">{num(f.areaProj)} m² proiettata · perimetro {num(f.perimetro)} m</div>
              {!selfToken && (
                <div className="flex items-center gap-1 mb-2 flex-wrap" onClick={(e) => e.stopPropagation()} data-testid={`roof-falda-cop-${idx}`}>
                  <Label className="text-[9px] uppercase tracking-wide text-muted-foreground mr-1">Copertura</Label>
                  <Button size="sm" variant={!f.copertura ? "default" : "outline"} className="h-6 px-2 text-[10px]" onClick={() => setFaldaCopertura(f.id, null)} data-testid={`roof-falda-cop-default-${idx}`}>Default ({roofType === "piano" ? "Piano" : "Inclinato"})</Button>
                  <Button size="sm" variant={f.copertura === "inclinato" ? "default" : "outline"} className="h-6 px-2 text-[10px]" onClick={() => setFaldaCopertura(f.id, "inclinato")} data-testid={`roof-falda-cop-inclinato-${idx}`}>Inclinato</Button>
                  <Button size="sm" variant={f.copertura === "piano" ? "default" : "outline"} className="h-6 px-2 text-[10px]" onClick={() => setFaldaCopertura(f.id, "piano")} data-testid={`roof-falda-cop-piano-${idx}`}>Piano</Button>
                </div>
              )}
              {(f.copertura || roofType) !== "piano" ? (
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[9px] uppercase tracking-wide text-muted-foreground">Orient.</Label>
                  <Select value={String(f.azimuth_deg)} onValueChange={(v) => setFaldaField(f.id, "azimuth_deg", Number(v))}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`roof-orient-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ORIENT.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[9px] uppercase tracking-wide text-muted-foreground">Inclin.°</Label>
                  <Input type="number" value={f.tilt_deg} onChange={(e) => setFaldaField(f.id, "tilt_deg", e.target.value)} className="h-8 text-xs" data-testid={`roof-tilt-${idx}`} />
                </div>
                <div>
                  <Label className="text-[9px] uppercase tracking-wide text-muted-foreground">Margine m</Label>
                  <Input type="number" step="0.1" value={f.margin_m} onChange={(e) => setFaldaField(f.id, "margin_m", e.target.value)} className="h-8 text-xs" data-testid={`roof-margin-${idx}`} />
                </div>
              </div>
              <div>
                <Label className="text-[9px] uppercase tracking-wide text-muted-foreground">Disposizione pannelli</Label>
                <Select value={f.disposizione || "auto"} onValueChange={(v) => setFaldaField(f.id, "disposizione", v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`roof-disposizione-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (migliore)</SelectItem>
                    <SelectItem value="verticale">Verticale</SelectItem>
                    <SelectItem value="orizzontale">Orizzontale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              </div>
              ) : (
                <div className="text-[11px] text-muted-foreground italic" onClick={(e) => e.stopPropagation()}>Copertura piana · inclinazione e file gestite dal sistema PRSG (sezione "Layout tetto piano" qui sotto).</div>
              )}
              {(f.esclusioni || []).length > 0 && (
                <div className="mt-2 space-y-1" onClick={(e) => e.stopPropagation()}>
                  {f.esclusioni.map((e, ei) => (
                    <div key={e.id} className="flex items-center justify-between text-[11px] bg-red-500/10 text-red-500 rounded px-2 py-1">
                      <span>{EXCL_TIPI.find((t) => t.v === e.tipo)?.l || e.tipo} · {num(e.areaProj)} m²</span>
                      <button onClick={() => removeExcl(f.id, e.id)} className="hover:text-red-400" data-testid={`roof-excl-del-${idx}-${ei}`}><Trash2 className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {!selfToken && annotations.length > 0 && (
            <div className="rounded-lg border border-border p-3" data-testid="roof-annotations-list">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-2">Elementi di cantiere</div>
              <div className="space-y-1">
                {annotations.map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-xs rounded px-2 py-1" style={{ background: (ANNOT[a.tipo]?.color || "#888") + "1A" }} data-testid={`roof-annot-${a.id}`}>
                    <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: ANNOT[a.tipo]?.color }} /> {a.label}{a.size ? ` ${a.size.replace("x", "×")} m` : ""}</span>
                    <button onClick={() => removeAnnotation(a.id)} className="text-muted-foreground hover:text-destructive" data-testid={`roof-annot-del-${a.id}`}><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button onClick={save} disabled={saving} className="w-full font-semibold" data-testid="roof-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva misurazione
          </Button>
        </div>
      </div>
      </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value, accent, testid }) {
  return (
    <div className="rounded border border-border bg-background px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`font-heading font-bold ${accent ? "text-primary" : ""}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function RoofWizardSteps({ step }) {
  const steps = [{ n: 1, label: "Pannello" }, { n: 3, label: "Il tuo tetto" }];
  return (
    <div className="flex items-center gap-1.5 mb-4" data-testid="roof-wizard-steps">
      {steps.map((s, i) => {
        const done = step > s.n;
        const active = step === s.n;
        return (
          <Fragment key={s.n}>
            <div className="flex items-center gap-2 min-w-0" data-testid={`roof-wizard-step-${s.n}`}>
              <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors ${active ? "bg-primary text-primary-foreground ring-2 ring-primary/40 ring-offset-2 ring-offset-background" : done ? "bg-primary/80 text-primary-foreground" : "bg-secondary text-muted-foreground border border-border"}`}>
                {done ? <Check className="h-4 w-4" strokeWidth={3} /> : s.n}
              </div>
              <span className={`text-xs font-semibold truncate ${active ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className={`h-0.5 flex-1 min-w-[12px] rounded ${step > s.n ? "bg-primary/70" : "bg-border"}`} />}
          </Fragment>
        );
      })}
    </div>
  );
}

function PanelLayoutControls({ lead, roof, moduleInfo, onLayout, onLead, apiKey, selfToken, blockLayout = false }) {
  const pl = roof.panel_layout;
  const base = selfToken ? `/public/self/${selfToken}` : `/leads/${lead.id}`;
  const mod = moduleInfo || roof.module || {};
  const defW = mod.larghezza_mm ? +(mod.larghezza_mm / 1000).toFixed(3) : 1.0;
  const defH = mod.lunghezza_mm ? +(mod.lunghezza_mm / 1000).toFixed(3) : 1.7;
  const ce = lead.cost_estimate || {};
  const propCount = ce?.componenti?.pannello?.n_pannelli ?? null;
  const propKwp = ce?.pv_kwp ?? null;
  const [border, setBorder] = useState(Math.round((pl?.params?.border_m ?? 0.20) * 1000));
  const [gap, setGap] = useState(Math.round((pl?.params?.gap_m ?? 0.025) * 1000));
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editFs, setEditFs] = useState(false);
  const [drvBusy, setDrvBusy] = useState(false);
  const [confirmDrv, setConfirmDrv] = useState(false);
  const rootRef = useRef(null);
  const notifiedRef = useRef(false);

  // Self-service (free/configura/rete/partner): quando il layout è pronto la PRIMA volta,
  // porta la sezione in vista così l'utente non "si ferma all'area" senza vedere il layout.
  useEffect(() => {
    if (!selfToken) return;
    if (pl?.panels?.length && !notifiedRef.current) {
      notifiedRef.current = true;
      setTimeout(() => {
        try { rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (_) {}
      }, 450);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfToken, pl?.panels?.length]);

  const setDriver = async (active) => {
    setDrvBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/panel-layout/driver`, { active });
      if (data.roof?.panel_layout) onLayout(data.roof.panel_layout);
      onLead && onLead(data);
      toast.success(active ? "Dimensionamento economico aggiornato dal layout" : "Dimensionamento tornato automatico");
      setConfirmDrv(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDrvBusy(false); }
  };

  const generate = async (enabled, orientation = "auto") => {
    if (blockLayout && enabled) { toast.error("Scegli prima un pannello per generare il layout"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/panel-layout`, {
        enabled,
        panel_w_m: Number(defW), panel_h_m: Number(defH),
        border_m: Number(border) / 1000, gap_m: Number(gap) / 1000, orientation,
      });
      onLayout(data);
      if (!enabled) toast.success("Layout nascosto");
      else toast.success(`${orientation === "auto" ? "Layout generato" : "Disposizione " + orientation}: ${data.summary?.count} pannelli (${data.summary?.kwp} kWp)`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Funnel pubblico (free): genera automaticamente il layout migliore la prima volta,
  // così è "di default automatico". La funzione completa (orientamento + editor) resta disponibile sotto.
  const didAutoGen = useRef(false);
  useEffect(() => {
    if (didAutoGen.current || !selfToken || blockLayout) return;
    if (pl?.panels?.length) { didAutoGen.current = true; return; }
    if ((roof?.falde?.length || 0) > 0) {
      didAutoGen.current = true;
      generate(true, "auto");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfToken, roof?.falde?.length]);

  return (
    <div ref={rootRef} className="mt-5 border-t border-border pt-4" data-testid="panel-layout-controls">
      {selfToken && pl?.enabled && pl?.summary && pl?.panels?.length ? (
        <div className="mb-4 rounded-xl border-2 border-emerald-500/60 bg-emerald-50 dark:bg-emerald-950/30 px-4 py-3 animate-in fade-in slide-in-from-top-2 duration-500" data-testid="panel-layout-ready-banner">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-semibold text-sm">
            <CheckCircle2 className="h-5 w-5 shrink-0" strokeWidth={2} /> Layout pannelli generato automaticamente sul tuo tetto
          </div>
          <div className="mt-1.5 text-2xl font-bold text-emerald-800 dark:text-emerald-300" data-testid="panel-layout-ready-summary">
            {pl.summary.count} pannelli · {pl.summary.kwp} kWp
          </div>
          <div className="text-xs text-emerald-700/80 dark:text-emerald-400/80 mt-0.5">Puoi modificarlo qui sotto (orientamento, sposta/aggiungi/elimina) oppure procedere per ricevere la consulenza completa.</div>
        </div>
      ) : null}
      {selfToken ? (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-xs text-muted-foreground" data-testid="panel-layout-auto-note">
          <Grid3x3 className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primary" />
          <span>Il layout dei pannelli viene generato <b>automaticamente</b> sul tuo tetto. Puoi comunque scegliere l'orientamento e spostare/aggiungere/eliminare i pannelli qui sotto.</span>
        </div>
      ) : null}
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><Grid3x3 className="h-4 w-4 text-primary" strokeWidth={1.5} /> Layout pannelli ipotetico</h3>
        {pl?.enabled ? (
          <Button variant="outline" size="sm" onClick={() => generate(false)} disabled={busy} data-testid="panel-layout-hide-btn">Nascondi</Button>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground mb-3">Disposizione automatica indicativa (la migliore statisticamente), mostrata in proposta e PDF. Non sostituisce il sopralluogo.</p>
      <PanelInUse module={{ ...(pl?.module || {}), ...(moduleInfo || roof.module || {}) }} freeArea={!!selfToken} className="mb-3" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
        <div><Label className="text-xs">Base pannello (m)</Label><Input type="number" value={defW} readOnly disabled title="Dimensione presa dal prodotto (non modificabile)" data-testid="panel-w-input" /></div>
        <div><Label className="text-xs">Altezza pannello (m)</Label><Input type="number" value={defH} readOnly disabled title="Dimensione presa dal prodotto (non modificabile)" data-testid="panel-h-input" /></div>
        <div><Label className="text-xs">Bordo dal perimetro (mm)</Label><Input type="number" step="5" value={border} onChange={(e) => setBorder(e.target.value)} data-testid="panel-border-input" /></div>
        <div><Label className="text-xs">Gap tra pannelli (mm)</Label><Input type="number" step="5" value={gap} onChange={(e) => setGap(e.target.value)} data-testid="panel-gap-input" /></div>
      </div>
      <ul className="text-[11px] text-muted-foreground mb-3 space-y-0.5 list-disc pl-4">
        <li><span className="font-medium text-foreground/80">Base × Altezza</span>: dimensioni del pannello, prese dal prodotto{mod.model ? ` (${mod.model})` : ""}.</li>
        <li><span className="font-medium text-foreground/80">Bordo dal perimetro</span>: distanza minima che i pannelli mantengono dai bordi della falda (perimetro del tetto).</li>
        <li><span className="font-medium text-foreground/80">Gap tra pannelli</span>: spazio lasciato tra un pannello e quello adiacente. La distanza dagli ingombri, invece, può arrivare a zero (pannelli a filo).</li>
      </ul>
      <div className="flex justify-end">
        <Button size="default" onClick={() => generate(true, "auto")} disabled={busy || blockLayout} data-testid="panel-layout-generate-btn"
          className="font-bold shadow-lg shadow-primary/30 ring-2 ring-primary/40 ring-offset-2 ring-offset-background hover:shadow-primary/40 transition-shadow">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Grid3x3 className="h-4 w-4 mr-1.5" strokeWidth={2} />}
          {pl?.panels?.length ? "Rigenera (migliore)" : "Genera layout"}
        </Button>
      </div>
      {pl?.enabled && pl?.variants && !(roof?.falde || []).some((f) => (f.disposizione || "auto") !== "auto") && (
        <div className="mt-3" data-testid="panel-orientation-choices">
          <p className="text-xs text-muted-foreground mb-1.5">Scegli la disposizione da usare/editare:</p>
          <div className="flex flex-wrap gap-2">
            {["verticale", "orizzontale"].map((o) => (
              <Button key={o} size="sm" variant={pl.chosen === o ? "default" : "outline"} disabled={busy || blockLayout}
                onClick={() => generate(true, o)} data-testid={`panel-orient-${o}`}>
                {o === "verticale" ? "Verticale" : "Orizzontale"} · {pl.variants[o]?.summary?.count ?? 0} pann.
              </Button>
            ))}
          </div>
        </div>
      )}
      {pl?.enabled && pl?.summary && (
        <div className="mt-3 text-sm" data-testid="panel-layout-summary">
          Selezionata: <span className="font-semibold">{pl.chosen === "orizzontale" ? "Orizzontale" : "Verticale"}</span> · <span className="font-semibold">{pl.summary.count}</span> pannelli · <span className="font-semibold">{pl.summary.kwp} kWp</span>
        </div>
      )}
      {pl?.enabled && pl?.summary && (propCount != null || propKwp != null) && (() => {
        const dC = propCount != null ? pl.summary.count - propCount : null;
        const dK = propKwp != null ? +(pl.summary.kwp - propKwp).toFixed(2) : null;
        const fmt = (v) => (v > 0 ? `+${v}` : `${v}`);
        const same = (dC === 0 || dC == null) && (dK === 0 || dK == null);
        const cls = (v) => (v > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400");
        return (
          <div className="mt-1.5 text-xs rounded-md border border-border bg-muted/40 px-2.5 py-2" data-testid="panel-layout-vs-proposal">
            <span className="text-muted-foreground">Rispetto alla proposta sviluppata finora ({propCount ?? "—"} pannelli{propKwp != null ? ` · ${propKwp} kWp` : ""}): </span>
            {same ? (
              <span className="font-medium text-emerald-600 dark:text-emerald-400">il layout coincide con il dimensionamento attuale.</span>
            ) : (
              <span className="font-medium">il layout {(dC ?? dK) > 0 ? "aumenta" : "riduce"} il dimensionamento
                {dC != null ? <> di <b className={cls(dC)}>{fmt(dC)} pannelli</b></> : null}
                {dK != null ? <> (<b className={cls(dK)}>{fmt(dK)} kWp</b>)</> : null}.
              </span>
            )}
          </div>
        );
      })()}
      {!selfToken && pl?.enabled && pl?.panels?.length > 0 && (
        <div className="mt-3 rounded-md border border-border p-3" data-testid="panel-driver-box">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium">Dimensionamento economico</p>
              <p className="text-xs text-muted-foreground">
                {pl.drives_sizing
                  ? `Guidato dal layout: ${pl.summary?.count} pannelli · ${pl.summary?.kwp} kWp`
                  : "Automatico (da consumi/superficie) — invariato"}
              </p>
            </div>
            {pl.drives_sizing
              ? <Button size="sm" variant="outline" onClick={() => setDriver(false)} disabled={drvBusy} data-testid="panel-driver-off">Disattiva</Button>
              : <Button size="sm" onClick={() => setConfirmDrv(true)} disabled={drvBusy} data-testid="panel-driver-on">Usa il layout</Button>}
          </div>
          {confirmDrv && !pl.drives_sizing && (
            <div className="mt-2 rounded bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900" data-testid="panel-driver-confirm">
              <p className="mb-2 text-amber-900">Attivando, il dimensionamento economico userà i <span className="font-semibold">{pl.summary?.count} pannelli</span> del layout ({pl.summary?.kwp} kWp) al posto del calcolo automatico. Il preventivo verrà ricalcolato. Confermi?</p>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setDriver(true)} disabled={drvBusy} data-testid="panel-driver-confirm-yes">{drvBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma"}</Button>
                <Button size="sm" variant="outline" className="bg-white text-amber-900 border-amber-300 hover:bg-amber-100 hover:text-amber-950" onClick={() => setConfirmDrv(false)} disabled={drvBusy} data-testid="panel-driver-confirm-no">Annulla</Button>
              </div>
            </div>
          )}
        </div>
      )}
      {pl?.enabled && pl?.panels?.length > 0 && (
        <div className="mt-3">
          {!editing ? (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="panel-edit-btn" className="mb-3">
                <Move className="h-4 w-4 mr-1" strokeWidth={1.5} /> Modifica (sposta / aggiungi / elimina)
              </Button>
              {(apiKey || hasDrawing(roof)) && (
                <div className="relative">
                  <RoofMapView falde={roof.falde} panelLayout={pl} panelPhoto={roofPanelPhoto(moduleInfo || roof.module)} pdfBg={roof.pdf_bg} origin={roof.center} drawMode={roof.draw_mode} nativeFullscreen={false} />
                  <Button size="sm" onClick={() => { setEditFs(true); setEditing(true); }} data-testid="panel-preview-fullscreen" className="absolute top-2 right-2 z-10 shadow-lg" title="Apri la modifica del layout a schermo intero">
                    <Maximize2 className="h-4 w-4 mr-1.5" /> Schermo intero
                  </Button>
                </div>
              )}
            </>
          ) : (
            <PanelLayoutEditor
              roof={roof} params={pl.params} chosen={pl.chosen} leadId={lead.id} selfToken={selfToken} startFullscreen={editFs}
              onSaved={(npl) => { onLayout(npl); setEditing(false); setEditFs(false); }}
              onClose={() => { setEditing(false); setEditFs(false); }}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default function RoofMap({ lead, onUpdate, selfToken, rete = false, aestheticPanel = false }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [roof, setRoof] = useState(lead.roof || null);
  const [moduleInfo, setModuleInfo] = useState(lead.roof?.module || null);
  const [loading, setLoading] = useState(!lead.roof);
  const [roofType, setRoofType] = useState(lead.roof?.roof_type || "inclinato");
  const stepped = !!selfToken;
  const [step, setStep] = useState(lead.roof?.falde?.length ? 3 : 1);

  useEffect(() => {
    // Carica SEMPRE il modulo attivo dal catalogo per avere dimensioni/potenza CORRENTI
    // (il roof.module salvato può essere una snapshot vecchia → il layout deve seguire il prodotto).
    if (selfToken) {
      api.get(`/public/self/${selfToken}/module`).then((r) => {
        const m = r.data || {};
        setModuleInfo({ model: m.model || null, area_m2: m.area_m2 || 2.2, potenza_wp: m.potenza_wp || 0, larghezza_mm: m.larghezza_mm || null, lunghezza_mm: m.lunghezza_mm || null, source: m.source || null, provided: !!m.provided, chosen: !!m.chosen });
      }).catch(() => setModuleInfo({ model: null, area_m2: 2.2, potenza_wp: 0 }))
        .then(() => (rete && !lead.roof ? api.get(`/public/self/${selfToken}/roof`).then((r) => {
          const saved = r.data || {};
          if (saved.falde?.length || saved.pdf_bg) { setRoof(saved); if (saved.roof_type === "inclinato" || saved.roof_type === "piano") setRoofType(saved.roof_type); }
        }).catch(() => {}) : null))
        .finally(() => setLoading(false));
      return;
    }
    api.get("/products").then((r) => {
      const p = (r.data || []).find((x) => x.category === "pannello" && x.active);
      if (p) {
        const area = p.larghezza_mm && p.lunghezza_mm ? (p.larghezza_mm * p.lunghezza_mm) / 1_000_000 : 2.2;
        setModuleInfo({ model: p.model, area_m2: +area.toFixed(3), potenza_wp: p.potenza_wp, larghezza_mm: p.larghezza_mm, lunghezza_mm: p.lunghezza_mm });
      } else if (!moduleInfo) {
        setModuleInfo({ model: null, area_m2: 2.2, potenza_wp: 0 });
      }
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const handleSaved = (data) => { setRoof(data); setModuleInfo((prev) => { const nm = data.module || prev; return nm ? { ...nm, chosen: (nm.chosen ?? prev?.chosen ?? false), provided: (nm.provided ?? prev?.provided) } : prev; }); if (data.roof_type === "inclinato" || data.roof_type === "piano") setRoofType(data.roof_type); onUpdate?.({ ...lead, roof: data }); };

  // Configuratore self/free e rete: la scelta del pannello è OBBLIGATORIA (nessun default).
  // "acquisito" = scelta esplicita (chosen) oppure pannello fornito automaticamente dalla rete.
  const panelAcquired = !!moduleInfo && (moduleInfo.chosen === true || (moduleInfo.source === "rete.torri.solar" && moduleInfo.provided));
  const needsPanel = stepped && !panelAcquired;

  return (
    <Card className="rounded border-border p-5" data-testid="roof-card">
      <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><MapIcon className="h-4 w-4 text-primary" strokeWidth={1.5} /> Misurazione tetto (satellite)</h2>
      <p className="text-xs text-muted-foreground mb-4">Cerca l'indirizzo, disegna le falde sul tetto e calcola le superfici disponibili.</p>
      {stepped && <RoofWizardSteps step={step} />}
      {!selfToken && lead?.id && (
        <ListinoPanelPicker leadId={lead.id} currentPanel={lead.listino_panel} roof={roof} onChanged={(mod) => onUpdate?.({ ...lead, listino_panel: mod })} className="mb-4" />
      )}
      {selfToken && (
        <div className={step === 1 ? "" : "hidden"}>
          {aestheticPanel ? (
            <AestheticPanelPicker selfToken={selfToken} currentModule={moduleInfo}
              onChanged={(mod) => setModuleInfo({ model: mod.model || mod.codice, area_m2: mod.area_m2, potenza_wp: mod.potenza_wp, larghezza_mm: mod.larghezza_mm, lunghezza_mm: mod.lunghezza_mm, photo_url: mod.photo_url, descrizione: mod.descrizione, source: "vetrina", chosen: true })}
              className="mb-3" />
          ) : (
            <FreePanelPicker selfToken={selfToken} currentModule={moduleInfo} roof={roof} rete={rete} required={stepped}
              onChanged={(mod) => setModuleInfo({ model: mod.model || mod.codice, area_m2: mod.area_m2, potenza_wp: mod.potenza_wp, larghezza_mm: mod.larghezza_mm, lunghezza_mm: mod.lunghezza_mm, photo_url: mod.photo_url, descrizione: mod.descrizione, source: "vetrina", chosen: true })}
              className="mb-3" />
          )}
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">{needsPanel ? "Scegli un pannello per continuare." : "Pannello selezionato. Prosegui per scegliere il tipo di tetto."}</p>
            <Button onClick={() => setStep(3)} disabled={needsPanel} data-testid="wizard-next-1" className="font-semibold shrink-0 shadow-lg shadow-primary/30">Avanti: il tuo tetto <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
          </div>
        </div>
      )}
      {!apiKey ? (
        <div className="text-sm text-warning bg-warning/10 border border-warning/30 rounded p-3">Google Maps non configurato (REACT_APP_GOOGLE_MAPS_API_KEY mancante).</div>
      ) : loading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : (
        <APIProvider apiKey={apiKey} libraries={["geometry", "places"]}>
          <RoofInner lead={lead} initialRoof={roof} moduleInfo={moduleInfo} onSaved={handleSaved} selfToken={selfToken} roofType={roofType} setRoofType={setRoofType} stepped={stepped} step={step} setStep={setStep} rete={rete} />
        </APIProvider>
      )}
      {(!stepped || step === 3) && roof?.falde?.length > 0 && needsPanel && !aestheticPanel && (
        <div className="mt-5 rounded-xl border-2 border-red-500/70 bg-red-500/10 px-4 py-3 flex items-start gap-2.5" data-testid="rete-panel-required-alert" role="alert">
          <TriangleAlert className="h-5 w-5 text-red-600 shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <div className="font-heading font-bold text-sm text-red-700 dark:text-red-400">Scelta del pannello obbligatoria</div>
            <div className="text-xs text-red-700/80 dark:text-red-400/80 mt-0.5">Seleziona un pannello qui sopra (pulsante «Cambia») per poter generare il layout. Non è previsto alcun pannello di default.</div>
          </div>
        </div>
      )}
      {selfToken && step === 3 && roof?.falde?.length > 0 && (roofType === "piano" ? (
        <FlatRoofControls
          lead={lead}
          roof={roof}
          moduleInfo={moduleInfo}
          apiKey={apiKey}
          selfToken={selfToken}
          blockLayout={needsPanel}
          onLayout={(fl) => { const nr = { ...roof, flat_layout: fl, roof_type: "piano" }; if (fl?.module) nr.module = { ...(roof.module || {}), ...fl.module }; setRoof(nr); onUpdate?.({ ...lead, roof: nr }); }}
        />
      ) : (
        <PanelLayoutControls
          lead={lead}
          roof={roof}
          moduleInfo={moduleInfo}
          apiKey={apiKey}
          selfToken={selfToken}
          blockLayout={needsPanel}
          onLead={onUpdate}
          onLayout={(pl) => { const nr = { ...roof, panel_layout: pl }; if (pl?.module) nr.module = pl.module; setRoof(nr); onUpdate?.({ ...lead, roof: nr }); }}
        />
      ))}
      {!selfToken && roof?.falde?.length > 0 && (() => {
        const eff = (f) => (f.copertura === "inclinato" || f.copertura === "piano") ? f.copertura : roofType;
        const hasInc = roof.falde.some((f) => eff(f) !== "piano");
        const hasPiano = roof.falde.some((f) => eff(f) === "piano");
        const cmb = combinedOf(roof);
        return (
          <div className="mt-5 space-y-4">
            {hasInc && hasPiano && (
              <div className="rounded-xl border-2 border-primary/40 bg-primary/5 p-4" data-testid="roof-combined-summary">
                <div className="text-[11px] uppercase tracking-widest text-primary font-bold mb-1">Totale progetto (combinato)</div>
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
                  <span className="font-heading font-bold text-lg" data-testid="roof-combined-total">{cmb.count} pannelli · {cmb.kwp} kWp</span>
                  <span className="text-muted-foreground">Inclinate: {cmb.inc.count} ({cmb.inc.kwp} kWp)</span>
                  <span className="text-muted-foreground">Piane: {cmb.piano.count} ({cmb.piano.kwp} kWp)</span>
                </div>
              </div>
            )}
            {hasInc && (
              <div data-testid="roof-section-inclinato">
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Layout falde inclinate</div>
                <PanelLayoutControls
                  lead={lead}
                  roof={roof}
                  moduleInfo={moduleInfo}
                  apiKey={apiKey}
                  selfToken={selfToken}
                  blockLayout={needsPanel}
                  onLead={onUpdate}
                  onLayout={(pl) => { const nr = { ...roof, panel_layout: pl }; if (pl?.module) nr.module = pl.module; setRoof(nr); onUpdate?.({ ...lead, roof: nr }); }}
                />
              </div>
            )}
            {hasPiano && (
              <div data-testid="roof-section-piano">
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Layout tetto piano (PRSG)</div>
                <FlatRoofControls
                  lead={lead}
                  roof={roof}
                  moduleInfo={moduleInfo}
                  apiKey={apiKey}
                  selfToken={selfToken}
                  blockLayout={needsPanel}
                  onLayout={(fl) => { const nr = { ...roof, flat_layout: fl }; if (fl?.module) nr.module = { ...(roof.module || {}), ...fl.module }; setRoof(nr); onUpdate?.({ ...lead, roof: nr }); }}
                />
              </div>
            )}
          </div>
        );
      })()}
    </Card>
  );
}
